// Écran de proximité (M17, lot L5-C1, H7-04) : quartiers du territoire avec représentants en exercice,
// besoins ouverts, engagements et consultations ; besoins priorisés de tous les quartiers ; carte des besoins
// localisés avec la couche existante des signalements quand l'agent peut la voir.
import { CATEGORIES_BESOIN, PRIORITES_BESOIN, type BesoinQuartier, type ResumeQuartier } from '@cc/contrats';
import { Carte, EtatVide, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useBesoins, useCoucheSignalements, useQuartiers } from './api';
import { libelleBesoin, TON_BESOIN } from './logique';
import styles from '../achats/achats.module.css';

const LIBREVILLE: [number, number] = [9.4536, 0.3901];

export function PageProximite() {
  const quartiers = useQuartiers();
  const besoins = useBesoins();
  const naviguer = useNavigate();
  const { contexte } = useSession();
  const voitSignalements = peut(contexte, 'intervention', 'voir');
  const signalements = useCoucheSignalements(voitSignalements);
  const listeQ = quartiers.data?.elements ?? [];
  const listeB = besoins.data?.elements ?? [];
  const localises = listeB.filter((b) => b.position && b.etat !== 'rejete');
  const colonnesQ: ColonneTableau<ResumeQuartier>[] = [
    { id: 'nom', entete: 'Quartier', enTeteLigne: true, masquable: false, cellule: (q) => <Lien href={`/proximite/quartiers/${q.id}`}>{q.nom}</Lien>, valeurTri: (q) => q.nom },
    { id: 'arrondissement', entete: 'Arrondissement', cellule: (q) => q.arrondissement?.nom ?? 'Non rattaché' },
    { id: 'representants', entete: 'Représentants en exercice', cellule: (q) => String(q.representants_en_exercice), valeurTri: (q) => q.representants_en_exercice },
    { id: 'besoins', entete: 'Besoins ouverts', cellule: (q) => String(q.besoins_ouverts), valeurTri: (q) => q.besoins_ouverts },
    { id: 'engagements', entete: 'Engagements en cours', cellule: (q) => String(q.engagements_en_cours), valeurTri: (q) => q.engagements_en_cours },
    { id: 'consultations', entete: 'Consultations ouvertes', cellule: (q) => String(q.consultations_ouvertes) },
  ];
  const colonnesB: ColonneTableau<BesoinQuartier>[] = [
    { id: 'numero', entete: 'Besoin', enTeteLigne: true, masquable: false, cellule: (b) => <Lien href={`/proximite/besoins/${b.id}`} className="cc-mono">{b.numero}</Lien>, valeurTri: (b) => b.numero },
    { id: 'titre', entete: 'Titre', cellule: (b) => `${b.titre}${b.fictif ? ' (fictif)' : ''}` },
    { id: 'quartier', entete: 'Quartier', cellule: (b) => b.quartier.nom },
    { id: 'categorie', entete: 'Catégorie', cellule: (b) => CATEGORIES_BESOIN[b.categorie] },
    { id: 'priorite', entete: 'Priorité', cellule: (b) => PRIORITES_BESOIN[b.priorite] },
    { id: 'etat', entete: 'État', cellule: (b) => <PuceEtat ton={TON_BESOIN[b.etat]} libelle={libelleBesoin(b.etat)} /> },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Proximité"
        titre="Quartiers et participation"
        description="Représentants désignés pour une période, réunions, besoins exprimés et leur orientation vers les signalements ou les projets, consultations et engagements de proximité."
      />
      <EtatRequete chargement={quartiers.isPending} erreur={quartiers.error} onReessayer={() => void quartiers.refetch()} lot="L5-C1" fonction="Les quartiers">
        <section className={styles.bloc} aria-labelledby="titre-quartiers">
          <h2 id="titre-quartiers">Quartiers</h2>
          {listeQ.length === 0 ? <EtatVide titre="Aucun quartier"><p>Aucun quartier n&apos;est enregistré dans le référentiel territorial de la collectivité.</p></EtatVide> : <Tableau legende="Quartiers" colonnes={colonnesQ} lignes={listeQ} cleLigne={(q) => q.id} cleColonnes="proximite-quartiers" />}
        </section>
      </EtatRequete>
      <EtatRequete chargement={besoins.isPending} erreur={besoins.error} onReessayer={() => void besoins.refetch()} lot="L5-C1" fonction="Les besoins des quartiers">
        <section className={styles.bloc} aria-labelledby="titre-besoins">
          <h2 id="titre-besoins">Besoins exprimés</h2>
          {listeB.length === 0 ? <EtatVide titre="Aucun besoin"><p>Aucun besoin n&apos;est encore exprimé.</p></EtatVide> : <Tableau legende="Besoins des quartiers, par priorité" colonnes={colonnesB} lignes={listeB} cleLigne={(b) => b.id} cleColonnes="proximite-besoins" />}
        </section>
        <section className={styles.bloc} aria-labelledby="titre-carte-besoins">
          <h2 id="titre-carte-besoins">Carte des besoins</h2>
          {localises.length === 0 ? <p className={styles.detail}>Aucun besoin localisé.</p> : (
            <Carte
              titre="Carte des besoins des quartiers"
              centre={localises[0]?.position ?? LIBREVILLE}
              zoom={13}
              couches={[
                { id: 'ouverts', libelle: 'Besoins à traiter', forme: 'cercle', ton: 'jaune', points: localises.filter((b) => b.etat !== 'traite').map((b) => ({ id: b.id, longitude: b.position![0], latitude: b.position![1], libelle: `${b.numero} : ${b.titre}` })) },
                { id: 'traites', libelle: 'Besoins traités', forme: 'carre', ton: 'vert', points: localises.filter((b) => b.etat === 'traite').map((b) => ({ id: b.id, longitude: b.position![0], latitude: b.position![1], libelle: `${b.numero} : ${b.titre}` })) },
                ...(voitSignalements ? [{ id: 'signalements', libelle: 'Signalements en cours (interventions)', forme: 'triangle' as const, ton: 'rouge' as const, points: (signalements.data?.features ?? []).map((f) => ({ id: f.id, longitude: f.geometry.coordinates[0], latitude: f.geometry.coordinates[1], libelle: `${f.properties.numero} : ${f.properties.categorie_libelle}` })) }] : []),
              ]}
              onPoint={(c, p) => void naviguer(c.id === 'signalements' ? `/interventions/signalements/${p.id}` : `/proximite/besoins/${p.id}`)}
            />
          )}
        </section>
      </EtatRequete>
    </>
  );
}
